import { Fragment, createElement } from "../unova";
import { DotStr, Empty, Space, StyleStr } from "../constants";
import {
  FunctionComponent,
  ElementsTypes,
  Props,
  ReactNode,
  JSX,
} from "../types";

import { extractFn, has, hashString, isObj, isStr } from "../utils";

function cache<T>(fn: (rules: CssMap[], hash: string) => T) {
  return function (data: string) {
    return data in _cacheData
      ? _cacheData[data]
      : (_cacheData[data] = fn(stringToObj(data), hashString(data)));
  };
}

const wrapper = (fn: (a: string) => string) => {
  return function template<P extends ElementsTypes, T>(
    value: TemplateStringsArray | string,
    attributes?: any,
    ...props: PropsFunction<P, T>
  ): string {
    return fn(
      isStr(value) ? value : templateToCssString(value, props, attributes)
    );
  };
};

function templateToCssString<P extends ElementsTypes, T>(
  value: TemplateStringsArray,
  props: PropsFunction<P, T>,
  attributes?: any
) {
  let cssString = "";
  for (let i = 0; i < value.length; i++) {
    cssString += value[i] + extractFn(attributes, props[i] as any);
  }
  return cssString.replace(cleanSingleComments, "");
}

const create = (name: string, isKeyframe = false, isRoot = false) =>
  cache((rules, id) => createStyle(rules, name, id, isKeyframe, isRoot));

export const css = wrapper(create(DotStr));
export const keyframes = wrapper(create("@keyframes ", true, false));
export const createGlobalStyle = wrapper(create(Empty, false, true));

const cssRules: string[] = [];

export const extract = () => cssRules.join(Empty);

const stringToObj = (cssString: string): CssMap[] => {
  const rules: CssMap[] = [];
  let match: RegExpExecArray | null = null;

  while ((match = format.exec(cssString)) != null) {
    const val = match[2];
    if (val) {
      rules.push({ k: val.trim(), v: stringToObj(cssString) });
    } else if (match[3]) {
      return rules;
    } else if (match[4]) {
      const attr = objAttr.exec(match[4]);
      if (attr) {
        rules.push({ k: attr[1].trim(), v: attr[2].trim() });
      }
    }
  }

  return rules;
};

function iterable(
  rules: CssMap[],
  name: string,
  selector: string,
  attributes: string[],
  isKeyframe = false
): string {
  return rules
    .map((item) => {
      const key = item.k;
      const value = item.v;
      if (isStr(value)) {
        return key + ":" + value + ";";
      } else if (isKeyframe) {
        return wrap(iterable(value, name, Empty, attributes), key);
      } else {
        const innerAttributes: string[] = [];
        const isSelector = has(key, ":");
        const hasAt = has(key, "@");
        const space = isSelector ? Empty : Space;
        const itemk = space + key;

        const hasKeyframe = hasAt && has(key, "k", 1);

        const initialParse = iterable(
          value,
          name ? (hasAt ? name : name + itemk) : key,
          hasAt ? key : Empty,
          innerAttributes,
          hasKeyframe
        );

        const finalParse = wrap(
          hasAt && !hasKeyframe ? wrap(initialParse, name) : initialParse,
          hasAt ? key : name + itemk
        ).trim();

        attributes.push(selector ? wrap(finalParse, selector) : finalParse);
        innerAttributes.forEach((item) => attributes.push(item));

        return Empty;
      }
    })
    .join(Empty);
}

function createStyle(
  rules: CssMap[],
  prefix: string,
  id: string,
  isKeyframe: boolean,
  isRoot: boolean
) {
  const name = isRoot ? Empty : prefix + id;
  const attributes: string[] = [];
  insertRule(wrap(iterable(rules, name, Empty, attributes, isKeyframe), name));
  attributes.forEach((item) => insertRule(item));
  return isRoot ? Fragment : id;
}

function insertRule(rule: string) {
  try {
    const sheet =
      isObj(window) &&
      document.head.appendChild(document.createElement(StyleStr)).sheet;
    if (sheet) {
      sheet.insertRule(rule, sheet.cssRules.length);
    } else {
      cssRules.push(rule);
    }
  } catch (_) {}
}

function wrap(stringToWrap: string, wrapper: string) {
  return wrapper + "{" + stringToWrap + "}";
}

type OptObject = Record<string, any>;
const _cacheData: OptObject = {};
const objAttr = /([^\:]+):([^\;]*);/;
const format =
  /(\/\*[\s\S]*?\*\/)|([^\s\;\{\}][^\;\{\}]*(?=\{))|(\})|([^\;\{\}]+\;(?!\s*\*\/))/gim;
const cleanSingleComments = /([^:]|^)\/\/.*$/gm;

interface CssMap {
  k: string;
  v: string | CssMap[];
}

type SComponent<TProps> = (
  props: TProps
) => ReactNode & { props: { className: string }; type: any };

function styledFactory<
  P extends ElementsTypes,
  Inherited extends SComponent<any>
>(nodeName: P | Inherited) {
  return <PP>(decls: TemplateStringsArray, ...props: PropsFunction<P, PP>) => {
    return (attr: StyledAttributes) => {
      const filteredAttributes: Props = {};

      for (const key in attr) {
        // if (key in _noop) {
        filteredAttributes[key] = attr[key as keyof JSX.IntrinsicAttributes];
        // }
      }

      filteredAttributes.className = (
        create(DotStr)(templateToCssString<P, PP>(decls, props, attr)) +
        Space +
        (attr.className || Empty) +
        (isStr(nodeName) ? Empty : Space + nodeName(props)?.props.className)
      ).trim();

      return createElement(
        isStr(nodeName) ? nodeName : nodeName(props).type,
        filteredAttributes,
        attr.children
      );
    };
  };
}

type PropsFunction<P extends ElementsTypes, PP> = Array<
  StaticProp | DynamicProp<P, PP>
>;

type StaticProp = string | number;
type DynamicProp<P extends ElementsTypes, PP> = (
  props: JSX.IntrinsicElements[P] & PP
) => StaticProp;

type StyledTag<P extends ElementsTypes> = <PP>(
  decls: TemplateStringsArray,
  ...props: PropsFunction<P, PP>
) => StyledComponent<JSX.IntrinsicElements[P] & PP>;

type StyledAttributes = {
  [Key in keyof JSX.IntrinsicElements]: JSX.IntrinsicElements[Key];
}[keyof JSX.IntrinsicElements];

type StyledComponent<T> = StyledAttributes &
  ([T] extends [FunctionComponent<any>] ? T : FunctionComponent<T>);

type IntrinsicsStyles = {
  readonly [P in ElementsTypes]: StyledTag<P>;
};

type Styled = typeof styledFactory & IntrinsicsStyles;

export const styled = new Proxy(styledFactory, {
  get(o, prop: ElementsTypes) {
    return o(prop);
  },
}) as Styled;

export default styled;
